import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvDelete } from '../api/dataverseClient';
import { ResourcePoolMember, listPoolMembers } from './resourcePoolService';
import { GroupMember, getGroupMembers } from './groupService';

// ── Project Resources ──────────────────────────────────────────────────
//
// A project resource is a link from a project to a TENANT RESOURCE POOL MEMBER.
// That linkage is the ONLY way a person becomes a project resource — there is no
// path to add a non-pool person (group member or not). Rate / hours-per-day /
// role are NEVER snapshotted here: they are read live from the linked pool member
// every time, so a later pool rate change is reflected automatically.
//
// Sync (dagrun_pm_projectresource is populated by):
//   • GroupSync — cross-checking the project's M365 Group roster against the pool
//     and auto-adding matches. ADDITIVE ONLY: never removes, even if someone
//     leaves the group later. Non-matches are surfaced to the PM, not added.
//   • Manual — PM adds an additional pool member not (or not yet) in the group.
// Removal is always manual + PM-driven (removeProjectResource), never automatic.

export type AddedVia = 'GroupSync' | 'Manual';
const ADDED_VIA_VALUE: Record<AddedVia, number> = { GroupSync: 100000000, Manual: 100000001 };
const ADDED_VIA_NAME: Record<number, AddedVia> = { 100000000: 'GroupSync', 100000001: 'Manual' };

interface ProjectResourceRow {
  dagrun_pm_projectresourceid: string;
  dagrun_pm_name: string | null;
  _dagrun_pm_projectid_value: string | null;
  _dagrun_pm_poolmemberid_value: string | null;
  dagrun_pm_addedvia: number | null;
  dagrun_pm_dateadded: string | null;
}

/** A project resource, enriched with LIVE pool-member fields (never cached). */
export interface ProjectResource {
  id: string;
  poolMemberId: string;
  addedVia: AddedVia;
  dateAdded: string | null;
  // live from the linked pool member (null when the pool member was removed):
  name: string;
  email: string | null;
  role: string | null;
  hoursPerDay: number | null;
  hourlyRate: number | null;
  poolMemberMissing: boolean;
}

export interface SyncResult {
  added: number;                       // newly-created GroupSync links this run
  nonPoolGroupMembers: GroupMember[];  // group members NOT in the pool (flagged)
  resources: ProjectResource[];        // full current list (post-sync), enriched
  poolMembers: ResourcePoolMember[];   // the tenant pool roster (for the picker)
}

const PR_FIELDS = [
  'dagrun_pm_projectresourceid', 'dagrun_pm_name',
  '_dagrun_pm_projectid_value', '_dagrun_pm_poolmemberid_value',
  'dagrun_pm_addedvia', 'dagrun_pm_dateadded',
];

async function listRaw(pca: IPublicClientApplication, projectId: string): Promise<ProjectResourceRow[]> {
  return dvList<ProjectResourceRow>(pca, 'dagrun_pm_projectresources', {
    select: PR_FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    top: 500,
  });
}

/** Match a group member to a pool member by AAD object id or email (case-insensitive). */
function matchPoolMember(gm: GroupMember, pool: ResourcePoolMember[]): ResourcePoolMember | null {
  const gmEmail = (gm.mail || gm.userPrincipalName || '').trim().toLowerCase();
  return pool.find(pm =>
    (!!pm.dagrun_pm_userid && !!gm.id && pm.dagrun_pm_userid === gm.id) ||
    (!!pm.dagrun_pm_useremail && !!gmEmail && pm.dagrun_pm_useremail.trim().toLowerCase() === gmEmail),
  ) ?? null;
}

function enrich(rows: ProjectResourceRow[], poolMembers: ResourcePoolMember[]): ProjectResource[] {
  const byId = new Map(poolMembers.map(m => [m.dagrun_pm_resourcepoolmemberid, m]));
  return rows.map(r => {
    const pm = r._dagrun_pm_poolmemberid_value ? byId.get(r._dagrun_pm_poolmemberid_value) : undefined;
    return {
      id: r.dagrun_pm_projectresourceid,
      poolMemberId: r._dagrun_pm_poolmemberid_value ?? '',
      addedVia: ADDED_VIA_NAME[r.dagrun_pm_addedvia ?? ADDED_VIA_VALUE.Manual] ?? 'Manual',
      dateAdded: r.dagrun_pm_dateadded,
      name: pm?.dagrun_pm_username ?? r.dagrun_pm_name ?? '(unknown)',
      email: pm?.dagrun_pm_useremail ?? null,
      role: pm?.dagrun_pm_role ?? null,
      hoursPerDay: pm?.dagrun_pm_defaulthoursperday ?? null,
      hourlyRate: pm?.dagrun_pm_defaulthourlyrate ?? null,
      poolMemberMissing: !pm,
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

async function createResourceLink(
  pca: IPublicClientApplication, projectId: string, poolMemberId: string, via: AddedVia, displayName: string | null,
): Promise<void> {
  await dvCreate(pca, 'dagrun_pm_projectresources', {
    dagrun_pm_name: (displayName || 'Resource').slice(0, 300),
    'dagrun_pm_ProjectId@odata.bind': `/dagrun_pm_projects(${projectId})`,
    'dagrun_pm_PoolMemberId@odata.bind': `/dagrun_pm_resourcepoolmembers(${poolMemberId})`,
    dagrun_pm_addedvia: ADDED_VIA_VALUE[via],
    dagrun_pm_dateadded: new Date().toISOString(),
  });
}

/**
 * Re-runnable group→pool sync + full listing. Called on Resources-section open
 * AND on the manual "Sync Group Members" button. Idempotent and additive:
 * only creates links for pool-matched group members not already linked; never
 * removes anything. Returns the flagged non-pool group members for the PM.
 */
export async function syncAndListProjectResources(
  pca: IPublicClientApplication,
  projectId: string,
  poolId: string,
  groupId: string | null,
): Promise<SyncResult> {
  const [poolMembers, groupMembers, existing] = await Promise.all([
    listPoolMembers(pca, poolId),
    groupId ? getGroupMembers(pca, groupId).catch(() => [] as GroupMember[]) : Promise.resolve([] as GroupMember[]),
    listRaw(pca, projectId),
  ]);

  const linkedPoolIds = new Set(
    existing.map(r => r._dagrun_pm_poolmemberid_value).filter((x): x is string => !!x),
  );

  const nonPoolGroupMembers: GroupMember[] = [];
  const toAdd: ResourcePoolMember[] = [];
  for (const gm of groupMembers) {
    const pm = matchPoolMember(gm, poolMembers);
    if (!pm) { nonPoolGroupMembers.push(gm); continue; }
    if (!linkedPoolIds.has(pm.dagrun_pm_resourcepoolmemberid)) {
      toAdd.push(pm);
      linkedPoolIds.add(pm.dagrun_pm_resourcepoolmemberid); // dedupe within this run
    }
  }

  for (const pm of toAdd) {
    try { await createResourceLink(pca, projectId, pm.dagrun_pm_resourcepoolmemberid, 'GroupSync', pm.dagrun_pm_username ?? pm.dagrun_pm_name); }
    catch { /* best effort — a failed add just resurfaces next sync */ }
  }

  const rows = toAdd.length ? await listRaw(pca, projectId) : existing;
  return {
    added: toAdd.length,
    nonPoolGroupMembers,
    resources: enrich(rows, poolMembers),
    poolMembers,
  };
}

/** PM manually adds a pool member (not from the group) as a project resource. */
export async function addPoolMemberToProject(
  pca: IPublicClientApplication, projectId: string, member: ResourcePoolMember,
): Promise<void> {
  await createResourceLink(pca, projectId, member.dagrun_pm_resourcepoolmemberid, 'Manual', member.dagrun_pm_username ?? member.dagrun_pm_name);
}

/** PM-driven removal. NEVER called by the group sync (additive-only). */
export async function removeProjectResource(
  pca: IPublicClientApplication, projectResourceId: string,
): Promise<void> {
  await dvDelete(pca, 'dagrun_pm_projectresources', projectResourceId);
}
